import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";

var _templateObject;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { identity, uniqBy } from 'lodash-es';
import { Col, Row } from 'antd';
import Badge from '../Badge';
import Button from '../Button';
import Skeleton from '../Skeleton';
import Space from '../Space';
import Typography from '../Typography';
import { TmsUIContext } from '../../TmsUIProvider';
import translations from './translations';
import { chunkNotificationList } from './utils';
import { NotificationBody, NotificationEmpty, NotificationHead, NotificationItem, NotificationItemContent, NotificationList, NotificationLoadMoreWrapper, NotificationSection, NotificationSectionTitle, NotificationWrapper } from './components';
var MAX_COL_LENGTH = 24;
var NotificationTabButton = styled(Button).withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !['useBorderBottom'].includes(prop);
  }
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  &.ant-btn {\n    width: 100%;\n    border-radius: 0;\n  }\n\n  &.ant-btn.ant-btn-text {\n    border-bottom: ", ";\n  }\n\n  &.ant-btn.ant-btn-text:focus,\n  &.ant-btn.ant-btn-text:hover {\n    border-bottom: 2.5px solid black;\n  }\n"])), function (props) {
  return props.useBorderBottom ? '2.5px solid black' : 'transparent';
});
/**
 * Tab data.
 * @typedef {Object} Tab
 * @property {string} key - Key to differentiate tabs.
 * @property {string} title - Title of tab.
 * @property {boolean} hasNextPage - Indicates whether or not it has data in next page.
 * @property {Notification[]} notificationList - List of notifications to be displayed.
 * @property {Function} onLoadMore - Callback when load more button is clicked.
 * @property {number} unseenCount - Number of unseen notifications.
 */

/**
 * Notification With Tab component for TMS platforms to show the list of notifications
 *
 * @param {Object} prop - NotificationWithTab props
 * @param {Function} prop.formatter - Function to format the text of notifications
 * @param {Boolean} prop.loading - Indicates whether or not it has finished fetching notifications data
 * @param {Boolean} prop.nextPageLoading - Indicates whether or not it has finished
 * fetching notifications in the next page
 * @param {Function} prop.onReadAll - Function to mark all the notifications as already read
 * @param {Function} prop.onSelect - Function to mark the selected notification as already read
 * @param {Tab[]} prop.tabs - Array of objects containing information of notifications based on types
 * @param {Number} prop.unreadCount - Number of unread notification
 * @returns {ReactElement} - NotificationWithTab component
 */
var NotificationWithTab = function NotificationWithTab(_ref) {
  var _ref$formatter = _ref.formatter,
      formatter = _ref$formatter === void 0 ? identity : _ref$formatter,
      _ref$loading = _ref.loading,
      loading = _ref$loading === void 0 ? false : _ref$loading,
      _ref$nextPageLoading = _ref.nextPageLoading,
      nextPageLoading = _ref$nextPageLoading === void 0 ? false : _ref$nextPageLoading,
      _ref$onReadAll = _ref.onReadAll,
      onReadAll = _ref$onReadAll === void 0 ? identity : _ref$onReadAll,
      _ref$onSelect = _ref.onSelect,
      onSelect = _ref$onSelect === void 0 ? identity : _ref$onSelect,
      _ref$tabs = _ref.tabs,
      tabs = _ref$tabs === void 0 ? [] : _ref$tabs,
      _ref$unreadCount = _ref.unreadCount,
      unreadCount = _ref$unreadCount === void 0 ? 0 : _ref$unreadCount;
  var currentDate = moment().format('YYYY-MM-DD');

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      selectedTabIndex = _useState2[0],
      setSelectedTabIndex = _useState2[1];

  var selectedTab = useMemo(function () {
    var tab = tabs[selectedTabIndex];
    return _objectSpread(_objectSpread({}, tab), {}, {
      notificationList: chunkNotificationList(uniqBy(tab.notificationList, 'key'))
    });
  }, [tabs, selectedTabIndex]);

  var _useContext = useContext(TmsUIContext),
      setDefaultTranslations = _useContext.setDefaultTranslations,
      translate = _useContext.translate; // Intended to invoke this function without using useEffect hook


  setDefaultTranslations(translations);
  return /*#__PURE__*/React.createElement(NotificationWrapper, null, /*#__PURE__*/React.createElement(NotificationHead, null, /*#__PURE__*/React.createElement(Typography.Text, {
    strong: true
  }, translate('common:notifications')), unreadCount > 0 && /*#__PURE__*/React.createElement(Button, {
    type: "link",
    onClick: onReadAll
  }, translate('common:mark_all_as_read'))), /*#__PURE__*/React.createElement(Row, null, tabs.map(function (tab, index) {
    return /*#__PURE__*/React.createElement(Col, {
      key: tab.key,
      span: MAX_COL_LENGTH / tabs.length
    }, /*#__PURE__*/React.createElement(NotificationTabButton, {
      onClick: function onClick() {
        return setSelectedTabIndex(index);
      },
      type: "text",
      useBorderBottom: selectedTabIndex === index
    }, /*#__PURE__*/React.createElement(Space, {
      justify: "center"
    }, /*#__PURE__*/React.createElement(Typography.Text, {
      strong: selectedTabIndex === index
    }, tab.title), /*#__PURE__*/React.createElement(Badge, {
      count: tab.unseenTabCount
    }))));
  })), /*#__PURE__*/React.createElement(NotificationBody, null, loading ? /*#__PURE__*/React.createElement(NotificationEmpty, null, /*#__PURE__*/React.createElement(Skeleton, {
    loading: loading,
    active: true,
    avatar: true
  }), /*#__PURE__*/React.createElement(Skeleton, {
    loading: loading,
    active: true,
    avatar: true
  }), /*#__PURE__*/React.createElement(Skeleton, {
    loading: loading,
    active: true,
    avatar: true
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, Object.keys(selectedTab.notificationList).map(function (notificationDate) {
    return /*#__PURE__*/React.createElement(NotificationSection, {
      key: notificationDate
    }, /*#__PURE__*/React.createElement(NotificationSectionTitle, null, /*#__PURE__*/React.createElement(Typography.Text, {
      strong: true,
      type: "secondary"
    }, currentDate === moment(notificationDate).format('YYYY-MM-DD') ? translate('common:today') : moment(notificationDate).format('DD MMMM YYYY'))), /*#__PURE__*/React.createElement(NotificationList, null, selectedTab.notificationList[notificationDate].map(function (notification) {
      return /*#__PURE__*/React.createElement(NotificationItem, {
        key: notification.key,
        isRead: notification.isRead,
        role: "button",
        onFocus: function onFocus(e) {
          return e;
        },
        onKeyPress: function onKeyPress(event) {
          return ['Enter', 'Space'].includes(event.key) && onSelect(notification);
        },
        onClick: function onClick() {
          return onSelect(notification);
        },
        useTabs: true
      }, /*#__PURE__*/React.createElement(NotificationItemContent, {
        useTabs: true
      }, /*#__PURE__*/React.createElement(Typography.Text, null, formatter(notification.content, notification)), /*#__PURE__*/React.createElement(Typography.Text, {
        type: "secondary"
      }, "".concat(moment(notification.time).format('HH:mm'), " WIB"))));
    })));
  }), Object.keys(selectedTab.notificationList).length < 1 && /*#__PURE__*/React.createElement(NotificationEmpty, {
    useTabs: true
  }, /*#__PURE__*/React.createElement(Typography.Text, {
    type: "secondary"
  }, translate('message:empty_notification'))), selectedTab.hasNextPage && /*#__PURE__*/React.createElement(NotificationLoadMoreWrapper, null, /*#__PURE__*/React.createElement(Button, {
    type: "link",
    block: true,
    loading: nextPageLoading,
    onClick: selectedTab.onLoadMore
  }, translate('common:load_more'))))));
};

export default NotificationWithTab;